import { useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { tableSkeletonRows } from "src/shared/components/layout/skeleton/TableSkeleton/TableSkeleton"
import FlexibleTable, { TableFilter } from "src/shared/components/data-display/FlexibleTable/FlexibleTable"
import Screen from "src/shared/components/layout/screen/Screen"
import TabMenu from "src/shared/components/navigation/tab-menu/TabMenu"
import { useHeader } from "./hooks/useHeader"
import { useModulos } from "src/shared/hooks/useModulos"
import "./HistorialRS.css"
import useGetOrdenes from "./hooks/useGetOrdenes"
import { HistorialEstadoOrden, Mesa, TipoOrden, useGetNumeroMesasQuery } from "src/gql/schema"
import useGetComandas from "./hooks/useGetComandas"
import { formatRestaurantRows, formatRoomServiceMostradorRows } from "./helpers/formatRows"
import TablePaginatorWrapper from "src/shared/components/data-display/FlexibleTable/sections/TablePaginatorWrapper/TablePaginatorWrapper"
import useCountHistorial from "./hooks/useCountHistorial"
import { useProfile } from "src/shared/hooks/useProfile"
import Search, { SearchRef } from "./components/search/Search"
import { useFormatDate } from "src/shared/hooks/useFormatDate"
import { monthNames } from "src/shared/components/forms/datapicker/date-picker"
import { CalendarButtons } from "src/shared/components/navigation/CalendarButtons/CalendarButtons"

function HistorialRS(): JSX.Element {
    const { restaurante } = useModulos()
    const { formatCustomDate, nowHotel, getHotelDateParts } = useFormatDate()
    const todayHotel = getHotelDateParts(nowHotel)
    const [type, setType] = useState<TipoOrden>(TipoOrden.RoomService)
    const [date, setDate] = useState<Date>(new Date(todayHotel.year, todayHotel.month, todayHotel.day))
    const [page, setpage] = useState(1)
    const { hotel_id } = useProfile()
    const searchRef = useRef<SearchRef>(null)
    const [numeroOrdenFilter, setnumeroOrdenFilter] = useState("")
    const [filterMesa, setfilterMesa] = useState<string[] | null>(null)
    const { data: mesas } = useGetNumeroMesasQuery({ variables: { hotel_id } })
    const headers = useHeader((mesas?.mesas as Mesa[]) || [], type)


    const { skeletonRows } = tableSkeletonRows({
        headers,
    })

    const navigate = useNavigate()
    const { countComandas, countOrdenesMostrador, countOrdenesRoomService } = useCountHistorial({
        dateFilter: date,
    })

    const {
        isLoading: isOrdenesLoading,
        ordenes,
        ordenesPages,
    } = useGetOrdenes({
        dateFilter: date,
        disabled: type === TipoOrden.Restaurante,
        numeroOrdenFilter,
        origenFilter: type,
        page,
    })

    const {
        comandas,
        comandasPages,
        isLoading: isComandasLoading,
    } = useGetComandas({
        dateFilter: date,
        disabled: type !== TipoOrden.Restaurante,
        numeroOrdenFilter,
        numeroMesa: filterMesa,
        page,
    })

    const handleFilter = (f: TableFilter[]) => {
        if (!f.length) {
            setfilterMesa(null)
            return
        }
        const mesaValues = f
            .filter(({ filter }) => filter !== "todos")
            .map(({ filter }) => filter)
        setfilterMesa(mesaValues.length ? mesaValues : null)
    }

    const handleSearch = (value: string) => {
        setnumeroOrdenFilter(value)
        setpage(1)
    }

    const [showGradient, setShowGradient] = useState(false);

    return (
        <Screen
            title={"Historial de movimientos"}
            className="ordenes__screen"
            contentClassName="ordenes"
            back={true}
            onBack={() => {
                navigate(-1)
            }}
            headerRight={
                <div className="ordenes__search">
                    <Search ref={searchRef} onChange={(e) => handleSearch(e)} />
                    <CalendarButtons
                        className="gastos-screen-header-controls"
                        currMonth={date.getMonth()}
                        setCurrMonth={(v) => {
                            if (v === 11 && date.getMonth() === 0) {
                                setDate(d => {
                                    const prev = new Date(d)
                                    prev.setFullYear(prev.getFullYear() - 1)
                                    prev.setDate(1)
                                    return prev
                                })
                            }
                            if (v === 0 && date.getMonth() === 11 && date.getFullYear() < todayHotel.year) {
                                setDate(d => {
                                    const next = new Date(d)
                                    next.setFullYear(next.getFullYear() + 1)
                                    next.setDate(1)
                                    return next
                                })
                            }
                            setDate(new Date(date.getFullYear(), v, 1))
                        }}
                        currDay={date.getDate()}
                        setCurrDay={(day) => {
                            setDate(d => {
                                const year = d.getFullYear()
                                const month = d.getMonth()
                                const next = new Date(year, month, day)
                                return next
                            })
                        }
                        }
                        currYear={date.getFullYear()}
                        setCurrYear={(year) => setDate(d => {
                            const newDate = new Date(d)
                            newDate.setFullYear(year)
                            newDate.setMonth(0)
                            newDate.setDate(1)
                            return newDate
                        })}
                        monthNames={monthNames}
                    />
                </div>
            }
        >
            <section className="ordenes__container">
                <div className="ordenes__container-filters">
                    <TabMenu
                        className="ordenes__container-tabs"
                        tabList={[
                            { label: "Room service", number: countOrdenesRoomService, path: TipoOrden.RoomService },
                            { label: "Mostrador", number: countOrdenesMostrador, path: TipoOrden.Mostrador },
                            ...(restaurante
                                ? [{ label: "Restaurante", number: countComandas, path: TipoOrden.Restaurante }]
                                : []),
                        ]}
                        value={type}
                        onChange={(value) => {
                            setType(value as TipoOrden)
                            searchRef.current?.setSearch("")
                            setpage(1)
                        }}
                        showNumerOnNoItems={true}
                    />
                </div>
                <TablePaginatorWrapper
                    pages={
                        type === TipoOrden.Restaurante ? comandasPages || 1 : ordenesPages || 1
                    }
                    currentPage={page}
                    onChange={(p) => setpage(p)}
                >
                    <div className="historial-rs__table">
                        <div className={`historial-rs__table-container${showGradient ? " historial-rs__table-container--show-gradient" : ""}`}>
                            <FlexibleTable
                                onSelectedFilters={handleFilter}
                                onHasScrollChange={setShowGradient}
                                tableItems={{
                                    ...{
                                        headers,
                                        rows:
                                            isComandasLoading || isOrdenesLoading
                                                ? skeletonRows
                                                : (type === TipoOrden.Restaurante
                                                    ? formatRestaurantRows(comandas, formatCustomDate)
                                                    : formatRoomServiceMostradorRows(ordenes as HistorialEstadoOrden[], formatCustomDate)
                                                )?.map((row) => ({
                                                    value: row?.value?.map(({ value }, index) => ({
                                                        value:
                                                            index === (type === TipoOrden.Restaurante ? 7 : 6) ? (
                                                                value
                                                            ) : index === (type === TipoOrden.Restaurante ? 8 : 7) ? (
                                                                value
                                                            ) : index ===
                                                                (type !== TipoOrden.Restaurante ? 8 : "") ? (
                                                                value
                                                            ) : index === 10 ? (
                                                                value
                                                            ) : (
                                                                value
                                                            ),
                                                    })),
                                                })) || [],
                                    },
                                }}
                                emptyState={{
                                    titile: "No tienes órdenes registradas",
                                    headerIcon: "dollarCircle",
                                }}
                            />
                        </div>
                    </div>
                </TablePaginatorWrapper>
            </section>
        </Screen>
    )
}

export default HistorialRS
